import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, CheckCircle2, AlertTriangle, Clock, BookOpen, Loader2 } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { AdminLayout } from "../../components/layout/AdminLayout";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const statusStyle = {
  completed: { bg: "bg-green-500/10", text: "text-green-600", border: "border-green-500/20", icon: CheckCircle2 },
  overdue: { bg: "bg-red-500/10", text: "text-red-600", border: "border-red-500/20", icon: AlertTriangle },
  in_progress: { bg: "bg-blue-500/10", text: "text-blue-600", border: "border-blue-500/20", icon: Clock },
  pending: { bg: "bg-amber-500/10", text: "text-amber-600", border: "border-amber-500/20", icon: Clock },
};

const LearnerCompliancePage = () => {
  const { user } = useAuth();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get("/my/compliance");
        setItems(res.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const summary = {
    total: items.length,
    completed: items.filter(i => i.status === "completed").length,
    overdue: items.filter(i => i.status === "overdue").length,
    pending: items.filter(i => i.status === "pending" || i.status === "in_progress").length,
  };
  const complianceRate = summary.total > 0 ? Math.round(summary.completed / summary.total * 100) : 0;

  return (
    <AdminLayout title="My Compliance" variant={variant}>
      <div className="space-y-6" data-testid="learner-compliance-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>My Compliance</h1>
          <p className="text-muted-foreground text-sm">Track your mandatory compliance training requirements</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "Total", value: summary.total, color: "text-blue-500", bg: "bg-blue-50" },
                { label: "Completed", value: summary.completed, color: "text-green-500", bg: "bg-green-50" },
                { label: "Pending", value: summary.pending, color: "text-amber-500", bg: "bg-amber-50" },
                { label: "Overdue", value: summary.overdue, color: "text-red-500", bg: "bg-red-50" },
              ].map((s, i) => (
                <Card key={i}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Overall Compliance Rate */}
            <Card>
              <CardContent className="py-4 px-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" /> Compliance Rate
                  </span>
                  <span className={`text-xl font-bold ${complianceRate >= 80 ? "text-green-500" : complianceRate >= 50 ? "text-amber-500" : "text-red-500"}`}>
                    {complianceRate}%
                  </span>
                </div>
                <Progress value={complianceRate} className="h-2.5" />
              </CardContent>
            </Card>

            {/* Compliance Items */}
            {items.length === 0 ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No compliance items assigned</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {items.map((item, i) => {
                  const style = statusStyle[item.status] || statusStyle.pending;
                  const IconComp = style.icon;
                  const progress = item.course_count > 0 ? Math.round(item.completed_courses / item.course_count * 100) : 0;
                  return (
                    <motion.div key={item.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                      <Card className={`border ${item.status === "overdue" ? "border-red-500/30" : ""}`} data-testid={`compliance-item-${i}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                              <IconComp className={`w-5 h-5 ${style.text}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-sm">{item.template_title}</h4>
                                {item.is_mandatory && (
                                  <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px]">Required</Badge>
                                )}
                                <Badge className={`${style.bg} ${style.text} ${style.border} text-[10px]`}>
                                  {item.status?.replace("_", " ")}
                                </Badge>
                              </div>
                              {item.description && (
                                <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{item.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <BookOpen className="w-3 h-3" /> {item.completed_courses}/{item.course_count} courses
                                </span>
                                <span>Due: {item.due_date}</span>
                              </div>
                              {item.course_count > 0 && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Progress value={progress} className="h-1.5 flex-1" />
                                  <span className="text-[10px] font-medium">{progress}%</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default LearnerCompliancePage;
